import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, Field, PageHeader, SelectField, StatusPill, type DataListColumn } from "@ozpanel/kit";
import {
  Button,
  Panel,
  Section,
  Stat,
  StateView,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useAuth,
  useToast,
} from "@ozpanel/ui";

// The firewall, read-only.
//
// Deliberately no apply/flush/policy controls here yet. The firewall is the one surface where a
// wrong click removes the only path back to the machine — there is a real lockout on record, and
// /nftables/apply carries a 20-second auto-revert for exactly that reason. Shipping the READING
// first is not a shortcut: an operator who cannot see what is exposed has no basis for changing
// it, and today the v2 console showed nothing at all.

interface Audit {
  safe: boolean;
  source: string;
  your_address: string;
  note: string;
  exposure: unknown[] | null;
  lockout: unknown[] | null;
}
interface PortRow {
  port: number;
  proto: string;
  /**
   * The addresses this port is bound to — an ARRAY on the wire (nftables/binds.go's
   * `Binds []string`), in all three of ports, mismatched and undeclared.
   *
   * Declared `string` and rendered directly, React concatenated the array with no separator, so
   * two bind addresses appeared as one run-together string ("0.0.0.0127.0.0.1") — wrong, silent,
   * and believable, on the screen whose entire job is showing what a service is reachable on.
   * CLAUDE.md's lockout history is the reason that screen has to be exactly right.
   */
  binds?: string[] | null;
  bind_scope?: string;
  bind_how?: string;
  bind_how_detail?: string;
  configured_bind?: string;
  configured_port?: number;
  actual_process?: string;
  actual_unit?: string;
  declared_by?: string;
  declared_unit?: string;
  enforced?: boolean;
  detail?: string;
  kind?: string;
}
/** One separator, one place. A bind list joined differently in three columns is three answers. */
function bindsText(b?: string[] | null): string {
  return (b ?? []).join(", ");
}

interface Undeclared {
  port: number;
  proto: string;
  process?: string;
  unit?: string;
  binds?: string[] | null;
  loopback?: boolean;
  ours?: boolean;
  detail?: string;
}
interface Enforcement {
  enforced: number;
  not_enforced: number;
  managed: number;
  managed_stated: number;
  not_listening: number;
  filtering: boolean;
  managed_absent: string[] | null;
  managed_unstated: string[] | null;
  ports: PortRow[] | null;
  undeclared: Undeclared[] | null;
  mismatched: PortRow[] | null;
  conflicts: unknown[] | null;
  note?: string;
}
interface PlanPort {
  port: number;
  proto: string;
  scope: string;
  exposure: string;
  owner: string;
  owner_title: string;
  help?: string;
  reach_help?: string;
}
interface Plan {
  ports: PlanPort[] | null;
  peers: string[] | null;
  admins: string[] | null;
  ruleset: string;
  template: string;
  invalid_reason: string;
  notes: string[] | null;
  how_to_apply?: string;
  how_to_change?: string;
}
interface Chain {
  name: string;
  policy?: string;
  rules?: unknown[] | null;
}
interface Table {
  family: string;
  name: string;
  handle?: number;
  chains: Chain[] | null;
}

interface AdminSource {
  id: number;
  cidr: string;
  label: string;
  enabled: boolean;
  scope: "all" | "console";
  covers_you: boolean;
}

interface AdminSources {
  restricting: boolean;
  you_covered: boolean;
  you_covered_console: boolean;
  console_restricted: boolean;
  your_address: string;
  sources: AdminSource[] | null;
  note?: string;
}

interface NodeSetting {
  node: string;
  key: string;
  value: string;
  in_force: boolean;
}

function AuditTab() {
  const { client } = useAuth();
  const audit = useQuery({
    queryKey: ["nft-audit"],
    queryFn: () => client.apiFetch<Audit>("/nftables/audit"),
  });
  const a = audit.data;
  return (
    <>
      <Section title="Lockout audit" hint={a?.source}>
        <StateView loading={audit.isLoading} error={audit.error} errorContext="the firewall audit">
          {a && (
            <>
              <div className="mb-3 flex flex-wrap gap-1.5">
                <StatusPill tone={a.safe ? "ok" : "bad"}>
                  {a.safe ? "no lockout found" : "LOCKOUT RISK"}
                </StatusPill>
                <StatusPill tone="neutral">you are {a.your_address}</StatusPill>
              </div>
              {(a.lockout ?? []).length > 0 && (
                <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] p-3 text-[12.5px]">
                  <b>This ruleset would cut off administrative access.</b>
                  <ul className="mt-1.5 list-disc pl-5">
                    {(a.lockout ?? []).map((l, i) => (
                      <li key={i}>{typeof l === "string" ? l : JSON.stringify(l)}</li>
                    ))}
                  </ul>
                </Panel>
              )}
              {(a.exposure ?? []).length > 0 && (
                <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
                  <b>Exposed beyond what is declared:</b>
                  <ul className="mt-1.5 list-disc pl-5">
                    {(a.exposure ?? []).map((l, i) => (
                      <li key={i}>{typeof l === "string" ? l : JSON.stringify(l)}</li>
                    ))}
                  </ul>
                </Panel>
              )}
              {/* The audit's own caveat, shown rather than buried. "safe" here means a static
                  reading found nothing — not that a packet was sent. Letting a green badge imply
                  a live test is the exact failure this note exists to prevent. */}
              <p className="text-[11.5px] leading-relaxed text-muted">{a.note}</p>
            </>
          )}
        </StateView>
      </Section>

      <AdminAccess />
    </>
  );
}

function PortsTab() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["nft-enforcement"],
    queryFn: () => client.apiFetch<Enforcement>("/nftables/enforcement"),
  });
  const d = q.data;

  const columns: DataListColumn<PortRow>[] = [
    {
      key: "port",
      label: "Port",
      sortable: true,
      value: (p) => p.port,
      render: (p) => (
        <span className="font-mono text-[12.5px]">
          {p.proto}/{p.port}
        </span>
      ),
    },
    { key: "actual_process", label: "Process", sortable: true, value: (p) => p.actual_process || "" },
    {
      key: "binds",
      label: "Binds to",
      sortable: true,
      value: (p) => bindsText(p.binds),
      render: (p) => <span className="font-mono text-[12px]">{bindsText(p.binds)}</span>,
    },
    {
      key: "bind_scope",
      label: "Scope",
      sortable: true,
      value: (p) => p.bind_scope || "",
      render: (p) =>
        p.bind_scope ? (
          <StatusPill tone={p.bind_scope === "public" ? "warn" : "ok"}>{p.bind_scope}</StatusPill>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: "enforced",
      label: "Firewall",
      sortable: true,
      value: (p) => (p.enforced ? "enforced" : "open"),
      // A service bound to every interface is private only while the firewall holds. Saying
      // which of the two is doing the work is the whole point of this table.
      render: (p) => <StatusPill tone={p.enforced ? "ok" : "warn"}>{p.enforced ? "enforced" : "not enforced"}</StatusPill>,
    },
  ];

  const undeclaredCols: DataListColumn<Undeclared>[] = [
    {
      key: "port",
      label: "Port",
      sortable: true,
      value: (p) => p.port,
      render: (p) => (
        <span className="font-mono text-[12.5px]">
          {p.proto}/{p.port}
        </span>
      ),
    },
    { key: "process", label: "Process", sortable: true, value: (p) => p.process || "" },
    { key: "unit", label: "Unit", sortable: true, value: (p) => p.unit || "" },
    {
      key: "loopback",
      label: "Reach",
      value: (p) => (p.loopback ? "loopback" : "network"),
      render: (p) => <StatusPill tone={p.loopback ? "ok" : "warn"}>{p.loopback ? "loopback only" : "on the network"}</StatusPill>,
    },
    { key: "ours", label: "Ours", value: (p) => (p.ours ? "yes" : "no"), render: (p) => (p.ours ? "yes" : <span className="text-muted">no</span>) },
  ];

  return (
    <StateView loading={q.isLoading} error={q.error} errorContext="port enforcement">
      {d && (
        <>
          <Section title="Summary">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Enforced" value={String(d.enforced)} />
              <Stat label="Not enforced" value={String(d.not_enforced)} tone={d.not_enforced > 0 ? "warn" : undefined} />
              <Stat label="Managed by ozpanel" value={String(d.managed)} />
              <Stat label="Declared in config" value={String(d.managed_stated)} />
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <StatusPill tone={d.filtering ? "ok" : "bad"}>
                {d.filtering ? "the firewall is filtering" : "NOT FILTERING — every port is open"}
              </StatusPill>
            </div>
            {/* A port ozpanel manages but never states in the service's own config is private
                only by firewall rule. That is the interlock: the declaration and the socket
                should agree, and where they do not, only one of them is holding. */}
            {(d.managed_unstated ?? []).length > 0 && (
              <Panel className="mt-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
                <b>{(d.managed_unstated ?? []).length} port(s) are private by firewall rule alone</b> — the service
                itself does not state the bind, so a ruleset that fails to apply exposes them.
                <ul className="mt-1.5 list-disc pl-5 font-mono text-[11.5px]">
                  {(d.managed_unstated ?? []).map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </Panel>
            )}
            {(d.managed_absent ?? []).length > 0 && (
              <Panel className="mt-3 border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] p-3 text-[12.5px]">
                <b>Declared but not present:</b> {(d.managed_absent ?? []).join(", ")}
              </Panel>
            )}
            {d.note && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">{d.note}</p>}
          </Section>

          <Section title="Listening ports" className="mt-6" hint={`${(d.ports ?? []).length} observed`} bodyClassName="p-0">
            <div className="p-4">
              <DataList
                items={d.ports ?? []}
                columns={columns}
                getRowKey={(p) => `${p.proto}/${p.port}/${p.actual_process ?? ""}`}
                searchable
                searchPlaceholder="Search ports, processes…"
                emptyMessage="Nothing is listening."
              />
            </div>
          </Section>

          {(d.mismatched ?? []).length > 0 && (
            <Section
              title="Declaration mismatches"
              className="mt-6"
              hint={`${(d.mismatched ?? []).length}`}
              bodyClassName="p-0"
            >
              <div className="p-4">
                <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
                  What the module declares and what is actually listening disagree. Both halves can look correct on
                  their own — this is the only place the disagreement is visible.
                </p>
                <DataList
                  items={d.mismatched ?? []}
                  columns={[
                    {
                      key: "port",
                      label: "Port",
                      sortable: true,
                      value: (p) => p.port,
                      render: (p) => (
                        <span className="font-mono text-[12.5px]">
                          {p.proto}/{p.port}
                        </span>
                      ),
                    },
                    { key: "declared_by", label: "Declared by", value: (p) => p.declared_by || "" },
                    { key: "actual_process", label: "Actually", value: (p) => p.actual_process || "" },
                    { key: "detail", label: "Detail", value: (p) => p.detail || "" },
                  ]}
                  getRowKey={(p) => `${p.proto}/${p.port}/m`}
                  emptyMessage=""
                />
              </div>
            </Section>
          )}

          {(d.undeclared ?? []).length > 0 && (
            <Section
              title="Undeclared listeners"
              className="mt-6"
              hint={`${(d.undeclared ?? []).length}`}
              bodyClassName="p-0"
            >
              <div className="p-4">
                <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
                  Listening, but no module declares them — so the derived firewall has no rule expressing what they
                  are for. A loopback-only listener is usually fine; one on the network is not.
                </p>
                <DataList
                  items={d.undeclared ?? []}
                  columns={undeclaredCols}
                  getRowKey={(p) => `${p.proto}/${p.port}/u`}
                  emptyMessage=""
                />
              </div>
            </Section>
          )}
        </>
      )}
    </StateView>
  );
}

// The DERIVED ruleset: what ozpanel would apply, and why each port is in it.
//
// The firewall is generated from what modules DECLARE, so the interesting question is not the
// nft syntax but which service asked for each opening. Shown read-only, with the live ruleset
// beside it: applying is deliberately not offered here — /nftables/apply audits for lockout and
// auto-reverts in 20 seconds unless confirmed, and wiring a button to that belongs with the
// confirm flow, not next to a table.
interface NetPolicy {
  outbound_default?: string;
  deny_ports?: number[] | null;
  allow_ports?: number[] | null;
  deny_cidrs?: string[] | null;
  allow_cidrs?: string[] | null;
  listen_ports?: number[] | null;
  /** One LAN host and port this account may reach although the private ranges are refused. */
  lan_exceptions?: { host: string; port: number; proto: string }[] | null;
  note?: string;
}
interface NetPolicyRow {
  tenant_id: number;
  tenant?: string;
  system_user?: string;
  uid?: number;
  policy?: NetPolicy;
  /** Where the policy came from: the platform default, the plan, or this account's override. */
  source?: string;
  /** Whether the kernel is actually carrying it — read back from nftables, not echoed. */
  in_force?: boolean;
  rules_in_kernel?: number;
  summary?: string;
}

/** Per-account network policy: what a customer's own processes may reach.
 *
 * The whole netpolicy module was unreachable — six routes, no console anywhere, so the outbound
 * abuse ports (25/465/587 by default) and the private-range blocks could be read from nftables and
 * changed only by editing the database or calling the API by hand.
 *
 * Two things this deliberately shows together. `source` says whether a policy is the platform
 * default, the plan's, or an override on this one account — a number with no provenance invites
 * an operator to "fix" an inherited value on the wrong object. `in_force` is read back OUT of
 * nftables rather than echoed from what was written, so a policy stored and not carried is
 * visible rather than reported as applied. */
function NetworkPolicyTab() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["netpolicy"],
    queryFn: () => client.apiFetch<{ items: NetPolicyRow[] | null; defaults?: NetPolicy }>("/network-policy"),
    retry: false,
  });
  const [edit, setEdit] = React.useState<NetPolicyRow | null>(null);

  const list = (v?: number[] | string[] | null) => (v ?? []).join(", ");

  const cols: DataListColumn<NetPolicyRow>[] = [
    { key: "tenant", label: "Account", sortable: true, render: (r) => <span className="font-medium">{r.tenant}</span> },
    {
      key: "source",
      label: "Policy from",
      render: (r) => <StatusPill tone={r.source === "account override" ? "warn" : "neutral"}>{r.source ?? "—"}</StatusPill>,
    },
    {
      key: "in_force",
      label: "Carried by the kernel",
      render: (r) => (
        <StatusPill tone={r.in_force ? "ok" : "bad"}>
          {r.in_force ? `yes — ${r.rules_in_kernel ?? 0} rules` : "NOT in force"}
        </StatusPill>
      ),
    },
    {
      key: "outbound",
      label: "Outbound",
      render: (r) => (
        <span className="text-[12px] text-muted">
          {r.policy?.outbound_default === "deny" ? "deny except allowed" : "allow except blocked"}
          {list(r.policy?.deny_ports) ? ` · blocks ${list(r.policy?.deny_ports)}` : ""}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <StateView loading={q.isLoading} error={q.error} errorContext="network policy">
        <Section title="Per-account network policy" bodyClassName="p-0">
          <div className="p-4">
            <p className="mb-3 max-w-3xl text-[12px] leading-relaxed text-muted">
              What each customer's own processes may reach off this node. The mail submission ports
              are blocked by default so a compromised site cannot send directly; loopback and this
              node's own addresses are always allowed, so a customer app can still use the
              platform's submission service, where the sending limits apply.
            </p>
            <DataList
              items={q.data?.items ?? []}
              columns={cols}
              getRowKey={(r) => String(r.tenant_id)}
              emptyMessage="No accounts."
              rowActions={(r) => (
                <Button size="sm" variant="ghost" onClick={() => setEdit(r)}>
                  edit
                </Button>
              )}
            />
          </div>
        </Section>
      </StateView>

      {edit && (
        <NetPolicyEditor
          row={edit}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            notify({ kind: "success", title: "Policy applied" });
            void qc.invalidateQueries({ queryKey: ["netpolicy"] });
          }}
        />
      )}
    </div>
  );
}

/** Edit one account's override. An EMPTY save restores what the plan says, which is why the
 *  "Remove the override" action is offered explicitly rather than left to be discovered by
 *  clearing every box. */
function NetPolicyEditor({ row, onClose, onSaved }: { row: NetPolicyRow; onClose: () => void; onSaved: () => void }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const p = row.policy ?? {};
  const [f, setF] = React.useState({
    outbound_default: p.outbound_default ?? "allow",
    deny_ports: (p.deny_ports ?? []).join(", "),
    allow_ports: (p.allow_ports ?? []).join(", "),
    deny_cidrs: (p.deny_cidrs ?? []).join(", "),
    allow_cidrs: (p.allow_cidrs ?? []).join(", "),
    listen_ports: (p.listen_ports ?? []).join(", "),
    // host:port/proto, comma separated — carried through every save, so editing another field never
    // drops an exception the server holds.
    lan_exceptions: (p.lan_exceptions ?? []).map((e) => `${e.host}:${e.port}/${e.proto}`).join(", "),
    note: p.note ?? "",
  });
  const [busy, setBusy] = React.useState(false);
  const nums = (v: string) => v.split(",").map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0);
  const strs = (v: string) => v.split(",").map((x) => x.trim()).filter(Boolean);
  const lan = (v: string) =>
    strs(v).map((x) => {
      const [hostPort = "", proto] = x.split("/");
      const i = hostPort.lastIndexOf(":");
      return { host: i > 0 ? hostPort.slice(0, i) : hostPort, port: i > 0 ? Number(hostPort.slice(i + 1)) : 0, proto: (proto || "tcp").trim() };
    });

  // The two routes take DIFFERENT bodies and the difference is not guessable, so it is written
  // down where the body is built: a PLAN takes a bare Policy, an ACCOUNT takes it wrapped as
  // {"policy": …} and is cleared with {"clear": true}. The field-reach check reported exactly
  // this — flat `allow_cidrs` for plans, `policy.allow_cidrs` for tenants — and the first version
  // of this screen sent the flat shape to both. It answered 200 and did the OPPOSITE of what was
  // asked: an unwrapped body decodes to a nil Policy, which is the signal to REMOVE the override.
  // Measured on node10 before it shipped, which is the only reason it is not in production.
  const save = async (body: unknown) => {
    setBusy(true);
    try {
      await client.apiFetch(`/network-policy/tenants/${row.tenant_id}`, { method: "PUT", body: JSON.stringify(body) });
      onSaved();
    } catch (e) {
      notify({ kind: "error", title: "Could not apply it", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const T = ({ k, label, ph }: { k: keyof typeof f; label: string; ph?: string }) => (
    <Field mono label={label} value={String(f[k])} placeholder={ph} onChange={(v) => setF({ ...f, [k]: v })} />
  );

  const denyMode = f.outbound_default === "deny";
  return (
    <Section title={`Network policy — ${row.tenant ?? row.tenant_id}`}>
      <div className="space-y-3">
        <SelectField
          label="Connections this account makes"
          value={f.outbound_default}
          onChange={(v) => setF({ ...f, outbound_default: v })}
          options={[
            { value: "allow", label: "allow, except what is blocked below" },
            { value: "deny", label: "deny, except what is allowed below" },
          ]}
        />

        <div className="grid gap-2 sm:grid-cols-2">
          {/* Only the pair that APPLIES in the chosen mode is shown. Both at once is how an
              operator fills in an allow-list that the mode makes inert. */}
          {denyMode ? (
            <>
              <T k="allow_ports" label="Ports it may reach" ph="80, 443" />
              <T k="allow_cidrs" label="Destinations it may reach" ph="203.0.113.0/24" />
            </>
          ) : (
            <>
              <T k="deny_ports" label="Ports it may never reach" ph="25, 465, 587" />
              <T k="deny_cidrs" label="Destinations it may never reach" ph="10.0.0.0/8" />
            </>
          )}
          <T k="listen_ports" label="Ports its own apps may bind" ph="8080" />
          <T k="lan_exceptions" label="LAN hosts it may reach (host:port/proto)" ph="192.168.88.93:3000/tcp" />
          <T k="note" label="Why (kept with the override)" ph="customer runs their own relay" />
        </div>
        <p className="max-w-3xl text-[11.5px] leading-relaxed text-muted">
          Blocking the mail submission ports is what stops a compromised site sending spam
          directly. Removing them from the block list lets it talk to any mail server on the
          internet, and the platform's sending limits no longer see that mail at all.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={busy}
            onClick={() =>
              void save({
                policy: {
                  outbound_default: f.outbound_default,
                  deny_ports: nums(f.deny_ports),
                  allow_ports: nums(f.allow_ports),
                  deny_cidrs: strs(f.deny_cidrs),
                  allow_cidrs: strs(f.allow_cidrs),
                  listen_ports: nums(f.listen_ports),
                  lan_exceptions: lan(f.lan_exceptions),
                  note: f.note,
                },
              })
            }
          >
            {busy ? "Applying…" : "Apply to this account"}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void save({ clear: true })}>
            Remove the override (use the plan's)
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Section>
  );
}

function PlanTab() {
  const { client } = useAuth();
  const plan = useQuery({ queryKey: ["nft-plan"], queryFn: () => client.apiFetch<Plan>("/nftables/plan") });
  const live = useQuery({ queryKey: ["nft-ruleset"], queryFn: () => client.apiFetch<{ tables: Table[] | null }>("/nftables/ruleset") });
  const d = plan.data;

  const cols: DataListColumn<PlanPort>[] = [
    {
      key: "port",
      label: "Port",
      sortable: true,
      value: (p) => p.port,
      render: (p) => <span className="font-mono text-[12.5px]">{p.proto}/{p.port}</span>,
    },
    { key: "owner_title", label: "Declared by", sortable: true, value: (p) => p.owner_title || p.owner },
    {
      key: "exposure",
      label: "Exposure",
      sortable: true,
      value: (p) => p.exposure,
      render: (p) => (
        <StatusPill tone={p.exposure === "public" ? "warn" : p.exposure === "private" ? "ok" : "neutral"}>
          {p.exposure}
        </StatusPill>
      ),
    },
    { key: "scope", label: "Reach", sortable: true, value: (p) => p.scope },
    { key: "help", label: "Why it is open", value: (p) => p.help || p.reach_help || "", render: (p) => <span className="text-[11.5px] leading-relaxed text-muted">{p.help || p.reach_help}</span> },
  ];

  const tableCols: DataListColumn<Table>[] = [
    { key: "name", label: "Table", sortable: true, render: (t) => <span className="font-mono text-[12.5px]">{t.family} {t.name}</span> },
    {
      key: "chains",
      label: "Chains",
      value: (t) => (t.chains ?? []).length,
      render: (t) => (
        <span className="flex flex-wrap gap-1.5">
          {(t.chains ?? []).map((c) => (
            <span key={c.name} className="rounded bg-bg2 px-1.5 py-0.5 font-mono text-[11px]">
              {c.name}
              {/* The base-chain POLICY is the whole posture: an input chain that defaults to
                  accept is not a firewall, however many rules sit above it. */}
              {c.policy && (
                <span className={c.policy === "drop" ? " text-ok" : " text-crit"}> {c.policy}</span>
              )}
              <span className="text-faint"> {(c.rules ?? []).length}</span>
            </span>
          ))}
        </span>
      ),
    },
  ];

  return (
    <>
      <Section title="Derived plan" hint={d?.template}>
        <StateView loading={plan.isLoading} error={plan.error} errorContext="the firewall plan">
          {d && (
            <>
              {d.invalid_reason ? (
                <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] p-3 text-[12.5px]">
                  <b>This plan would not apply:</b> {d.invalid_reason}
                </Panel>
              ) : (
                <div className="mb-3">
                  <StatusPill tone="ok">the plan is valid</StatusPill>
                </div>
              )}
              <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12.5px]">
                <dt className="text-muted">Cluster peers allowed</dt>
                <dd className="font-mono">{(d.peers ?? []).join(", ") || "none"}</dd>
                <dt className="text-muted">Admin sources</dt>
                <dd className="font-mono">{(d.admins ?? []).join(", ") || "unrestricted"}</dd>
              </dl>
              {(d.notes ?? []).map((n, i) => (
                <p key={i} className="mb-1.5 text-[11.5px] leading-relaxed text-muted">{n}</p>
              ))}
            </>
          )}
        </StateView>
      </Section>

      <Section title="Openings this plan creates" className="mt-6" hint={d ? `${(d.ports ?? []).length}` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <DataList
            items={d?.ports ?? []}
            columns={cols}
            getRowKey={(p) => `${p.proto}/${p.port}/${p.owner}`}
            searchable
            searchPlaceholder="Search ports, services…"
            loading={plan.isLoading}
            emptyMessage="The plan opens nothing."
          />
        </div>
      </Section>

      <Section title="Live ruleset" className="mt-6" hint="what nft is running now" bodyClassName="p-0">
        <div className="p-4">
          <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
            Read from <code className="font-mono">nft list ruleset</code>. Tables ozpanel does not own — the container
            runtime's (Podman/netavark) and CrowdSec's — appear here too, because they are part of what this machine actually enforces.
          </p>
          <DataList
            items={live.data?.tables ?? []}
            columns={tableCols}
            getRowKey={(t) => `${t.family}/${t.name}`}
            loading={live.isLoading}
            error={live.error}
            errorContext="the live ruleset"
            emptyMessage="No tables."
          />
        </div>
      </Section>
    </>
  );
}

import { FirewallAdmin } from "./FirewallAdmin";

// ExceptionsTab — every opening an account's processes get beyond the default policy, in one place (Oz,
// 2026-10-18): the operator's per-account LAN exceptions and overrides, plans that differ from the default,
// the openings services declare for the accounts they name, and the platform-wide ones. Read from the same
// inputs the ruleset is rendered from (GET /network-policy/exceptions). Edited where each is decided: an
// account's on the "Per-account policy" tab; a service's by that service.
interface FwException {
  kind: string;
  applies_to: string;
  tenant_id?: number;
  what: string;
  destination?: string[] | null;
  ports?: string;
  declared_by: string;
  why?: string;
  direction?: string;
}
const exceptionKind: Record<string, { label: string; tone: "warn" | "neutral" | "accent" | "ok" }> = {
  lan_exception: { label: "LAN exception", tone: "warn" },
  account_policy: { label: "account override", tone: "warn" },
  plan_policy: { label: "plan policy", tone: "accent" },
  service_grant: { label: "service grant", tone: "neutral" },
  platform: { label: "platform-wide", tone: "ok" },
  // Inbound: an approved app port open to the internet. A Podman-published port goes through the
  // FORWARD chain, so the node's firewall rules never show it — this list is where it is named.
  published_port: { label: "published port (inbound)", tone: "warn" },
};

function ExceptionsTab() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["netpolicy-exceptions"],
    queryFn: () => client.apiFetch<{ items: FwException[] | null; total: number }>("/network-policy/exceptions"),
    retry: false,
  });
  const cols: DataListColumn<FwException>[] = [
    {
      key: "kind",
      label: "Kind",
      sortable: true,
      value: (r) => r.kind,
      render: (r) => <StatusPill tone={exceptionKind[r.kind]?.tone ?? "neutral"}>{exceptionKind[r.kind]?.label ?? r.kind}</StatusPill>,
    },
    { key: "applies_to", label: "Applies to", sortable: true, value: (r) => r.applies_to, render: (r) => <span className="font-medium">{r.applies_to}</span> },
    {
      key: "what",
      label: "Opens",
      render: (r) => (
        <span className="text-[12px]">
          {r.what}
          {(r.destination ?? []).length > 0 && (
            <span className="block font-mono text-[11px] text-muted">
              {(r.destination ?? []).join(", ")}
              {r.ports ? ` · ${r.ports}` : ""}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "declared_by",
      label: "Declared by",
      render: (r) => (
        <span className="text-[12px] text-muted">
          {r.declared_by}
          {r.why && <span className="block text-[11px]">{r.why}</span>}
        </span>
      ),
    },
  ];
  return (
    <StateView loading={q.isLoading} error={q.error} errorContext="firewall exceptions">
      <Section title="Exceptions" hint={q.data ? `${q.data.total}` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <p className="mb-3 max-w-3xl text-[12px] leading-relaxed text-muted">
            Every opening customer accounts get beyond the default policy (no private networks, no mail
            ports off this node). Account and plan openings are set on the Per-account policy tab;
            service grants follow the service (e.g. Redis for accounts that have it). Published ports are the
            INBOUND openings: an account&apos;s app port approved to be reachable from the internet — decided on the
            port request, and not visible in the node&apos;s firewall rules because Podman forwards it directly.
          </p>
          <DataList
            items={q.data?.items ?? []}
            columns={cols}
            getRowKey={(r) => `${r.kind}|${r.applies_to}|${r.what}`}
            emptyMessage="No exceptions: every account runs the default policy."
          />
        </div>
      </Section>
    </StateView>
  );
}

export function FirewallScreen() {
  return (
    <div>
      <PageHeader title="Firewall" subtitle="What is exposed, what the firewall enforces, and whether you would be locked out" />
      <Tabs defaultValue="audit">
        <TabsList>
          <TabsTrigger value="audit">Audit</TabsTrigger>
          <TabsTrigger value="ports">Ports &amp; enforcement</TabsTrigger>
          <TabsTrigger value="plan">Plan &amp; ruleset</TabsTrigger>
          {/* The manual half. Last on purpose: the derived views answer most questions, and this
              is the only tab that can cut you off from the node. */}
          <TabsTrigger value="exceptions">Exceptions</TabsTrigger>
          <TabsTrigger value="accounts">Per-account policy</TabsTrigger>
          <TabsTrigger value="manage">Tables &amp; rules</TabsTrigger>
        </TabsList>
        <TabsContent value="audit">
          <AuditTab />
        </TabsContent>
        <TabsContent value="ports">
          <PortsTab />
        </TabsContent>
        <TabsContent value="plan">
          <PlanTab />
        </TabsContent>
        <TabsContent value="exceptions">
          <ExceptionsTab />
        </TabsContent>
        <TabsContent value="accounts">
          <NetworkPolicyTab />
        </TabsContent>
        <TabsContent value="manage">
          <FirewallAdmin />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Administrative access (F22a): the admin sources, editable, and the switch that restricts
// administrators to them. Both are cluster-wide. The switch is OFF by default and the server refuses
// to turn it on from an address the list does not cover.
const RESTRICT_KEY = "admin_console_restricted";

function AdminAccess() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const adm = useQuery({
    queryKey: ["nft-admin-sources"],
    queryFn: () => client.apiFetch<AdminSources>("/nftables/admin-sources"),
  });
  const settings = useQuery({
    queryKey: ["nft-settings"],
    queryFn: () =>
      client.apiFetch<{ cluster?: { nodes?: NodeSetting[] } }>("/nftables/settings"),
  });
  const [cidr, setCidr] = React.useState("");
  const [label, setLabel] = React.useState("");
  const [scope, setScope] = React.useState<"all" | "console">("console");
  const [busy, setBusy] = React.useState(false);
  const s = adm.data;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["nft-admin-sources"] });
    void qc.invalidateQueries({ queryKey: ["nft-settings"] });
  };
  const run = async (title: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      notify({ kind: "success", title });
      refresh();
    } catch (e) {
      notify({ kind: "error", title: "Not done", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  const add = () =>
    run("Address added", async () => {
      await client.apiFetch("/nftables/admin-sources", {
        method: "POST",
        body: JSON.stringify({ cidr: cidr.trim(), label: label.trim(), scope }),
      });
      setCidr("");
      setLabel("");
    });
  const remove = (src: AdminSource) => {
    if (src.covers_you && !window.confirm(`${src.cidr} covers your own address (${s?.your_address}). Remove it anyway?`)) return;
    void run("Address removed", () => client.apiFetch(`/nftables/admin-sources/${src.id}`, { method: "DELETE" }));
  };
  const setRestricted = (on: boolean) =>
    run(on ? "Administrators restricted to these addresses" : "Restriction turned off", () =>
      client.apiFetch("/nftables/settings", { method: "PUT", body: JSON.stringify({ [RESTRICT_KEY]: on }) }),
    );

  const nodes = (settings.data?.cluster?.nodes ?? []).filter((n) => n.key === RESTRICT_KEY);
  const sources = s?.sources ?? [];

  return (
    <Section title="Administrative access" className="mt-6">
      <StateView loading={adm.isLoading} error={adm.error} errorContext="admin sources">
        {s && (
          <>
            <div className="mb-3 flex flex-wrap gap-1.5">
              <StatusPill tone={s.restricting ? "ok" : "warn"}>
                {s.restricting ? "SSH + node ports: allowlist" : "SSH + node ports: open to everyone"}
              </StatusPill>
              {s.restricting && (
                <StatusPill tone={s.you_covered ? "ok" : "bad"}>
                  {s.you_covered ? "your address can SSH" : `YOUR ADDRESS (${s.your_address}) CANNOT SSH`}
                </StatusPill>
              )}
              <StatusPill tone={s.console_restricted ? "ok" : "neutral"}>
                {s.console_restricted ? "administrators: admin addresses only" : "administrators: from anywhere"}
              </StatusPill>
              <StatusPill tone="neutral">you are {s.your_address}</StatusPill>
            </div>

            <Panel className="mb-4 p-3 text-[12.5px]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <b>Administrators only from these addresses</b>
                  <p className="mt-0.5 text-[11.5px] text-muted">
                    When on, a super-admin can sign in and use the panel or API only from an address below (either
                    scope), a cluster node, or the node itself. Tenant, mailbox and webmail sign-ins are not affected.
                    Break-glass as root on any node: <code>ozpanel admin allow-ip &lt;address&gt;</code>.
                  </p>
                </div>
                {s.console_restricted ? (
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => setRestricted(false)}>
                    Turn off
                  </Button>
                ) : (
                  <Button size="sm" disabled={busy || !s.you_covered_console} onClick={() => setRestricted(true)}>
                    Turn on
                  </Button>
                )}
              </div>
              {!s.console_restricted && !s.you_covered_console && (
                <p className="mt-2 text-[11.5px] text-[var(--ozp-warn)]">
                  Add your own address ({s.your_address}) first — turning this on from an address the list does not
                  cover is refused, because it would lock you out.
                </p>
              )}
              {nodes.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {nodes.map((n) => (
                    <StatusPill key={n.node} tone={n.in_force ? "ok" : "warn"}>
                      {n.node}: {n.value === "true" ? "on" : "off"}
                      {n.in_force ? "" : " (not yet in force)"}
                    </StatusPill>
                  ))}
                </div>
              )}
            </Panel>

            {sources.length > 0 && (
              <table className="mb-3 w-full text-[12.5px]">
                <thead className="text-left text-[11px] uppercase text-muted">
                  <tr>
                    <th className="py-1">Address</th>
                    <th>Label</th>
                    <th>Opens</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sources.map((src) => (
                    <tr key={src.id} className="border-t border-border">
                      <td className="py-1.5 font-mono">
                        {src.cidr}
                        {src.covers_you && <span className="ml-1.5 text-[11px] text-muted">(you)</span>}
                      </td>
                      <td>{src.label}</td>
                      <td>
                        <StatusPill tone={src.scope === "all" ? "warn" : "neutral"}>
                          {src.scope === "all" ? "SSH, node ports + console" : "console only"}
                        </StatusPill>
                      </td>
                      <td className="text-right">
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove(src)}>
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
              <Field mono label="Address or network" value={cidr} placeholder="203.0.113.7 or 203.0.113.0/24" onChange={setCidr} />
              <Field label="Label" value={label} placeholder="who or what this is" onChange={setLabel} />
              <SelectField
                label="Opens"
                value={scope}
                onChange={(v) => setScope(v as "all" | "console")}
                options={[
                  { value: "console", label: "Administrator console only" },
                  { value: "all", label: "SSH, node ports and the console" },
                ]}
              />
              <Button size="sm" disabled={busy || !cidr.trim()} onClick={add}>
                Add
              </Button>
            </div>
            {s.note && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">{s.note}</p>}
          </>
        )}
      </StateView>
    </Section>
  );
}
